/**
 * buildValidationSql — Generates DuckDB SQL to validate all rows in a
 * materialized mapping view against target-schema validation rules.
 *
 * The summary query returns one row per rule with violation_count + total_rows.
 * The drill-down query returns the actual violating rows for a specific rule.
 */

// ── Types ────────────────────────────────────────────────────────────

export interface SchemaRule {
  /** Phase 3 typed-rule discriminator (``enum``, ``range``, ``regex``,
   *  ``length``, ``pattern``, ``format``, ``not_empty``, ``unique``). */
  type?: string;
  /** Legacy discriminator key, predates Phase 3. Backend no longer
   *  emits this — kept here for transitional reads of stored recipes. */
  rule?: string;
  pattern?: string;
  /** Phase 3 enum entries (``ValidationRule.values``). */
  values?: string[];
  /** Legacy enum entries — backend no longer emits; transitional reads only. */
  allowed_values?: string[];
  min_length?: number;
  max_length?: number;
  min?: number;
  max?: number;
  field?: string;
  severity?: string;
  message?: string;
}

/** Read the enum entries from a rule, preferring the Phase 3 ``values``
 *  field over the legacy ``allowed_values`` field. Returns an empty
 *  array when neither is populated. */
export function ruleEnumValues(rule: SchemaRule): string[] {
  const v = rule.values;
  if (Array.isArray(v) && v.length > 0) return v.map(String);
  const a = rule.allowed_values;
  if (Array.isArray(a) && a.length > 0) return a.map(String);
  return [];
}

export interface ColumnRules {
  columnName: string;
  rules: SchemaRule[];
  isNullable?: boolean;
  isPrimaryKey?: boolean;
}

export interface ValidationSummary {
  target_field: string;
  rule_type: string;
  severity: 'error' | 'warning' | 'info';
  violation_count: number;
  total_rows: number;
  message?: string;
}

// ── Alias normalization (mirrors backend RULE_TYPE_ALIASES) ──────────

const RULE_TYPE_ALIASES: Record<string, string> = {
  in_list: 'enum',
  pattern: 'regex',
  format: 'regex',
  datetime_format: 'date_format',
};

export function canonicalRuleType(rule: SchemaRule): string {
  const raw = (rule.rule || rule.type || '').toLowerCase().trim();
  return RULE_TYPE_ALIASES[raw] || raw;
}

export function defaultSeverity(ruleType: string, rule: SchemaRule): string {
  if (rule.severity) return rule.severity;
  if (ruleType === 'not_empty' || ruleType === 'nullable' || ruleType === 'primary_key') return 'error';
  return 'warning';
}

// ── SQL helpers ──────────────────────────────────────────────────────

function q(identifier: string): string {
  // Quote a DuckDB identifier
  return `"${identifier.replace(/"/g, '""')}"`;
}

function esc(value: string): string {
  // Escape a SQL string literal
  return `'${value.replace(/'/g, "''")}'`;
}

// ── WHERE clause builders per rule type ──────────────────────────────

function whereClause(col: string, ruleType: string, rule: SchemaRule): string | null {
  const c = q(col);

  switch (ruleType) {
    case 'not_empty':
      return `(${c} IS NULL OR TRIM(CAST(${c} AS VARCHAR)) = '')`;

    case 'enum': {
      const allowed = ruleEnumValues(rule);
      if (allowed.length === 0) return null;
      const inList = allowed.map((v) => `LOWER(${esc(String(v))})`).join(', ');
      return `(${c} IS NOT NULL AND LOWER(CAST(${c} AS VARCHAR)) NOT IN (${inList}))`;
    }

    case 'regex': {
      const pattern = rule.pattern;
      if (!pattern) return null;
      return `(${c} IS NOT NULL AND NOT regexp_matches(CAST(${c} AS VARCHAR), ${esc(pattern)}))`;
    }

    case 'length': {
      const minLen = rule.min_length ?? rule.min;
      const maxLen = rule.max_length ?? rule.max;
      if (minLen == null && maxLen == null) return null;
      const conditions: string[] = [`${c} IS NOT NULL`];
      const lengthChecks: string[] = [];
      if (minLen != null) lengthChecks.push(`LENGTH(CAST(${c} AS VARCHAR)) < ${Number(minLen)}`);
      if (maxLen != null) lengthChecks.push(`LENGTH(CAST(${c} AS VARCHAR)) > ${Number(maxLen)}`);
      conditions.push(`(${lengthChecks.join(' OR ')})`);
      return `(${conditions.join(' AND ')})`;
    }

    case 'range': {
      const minVal = rule.min;
      const maxVal = rule.max;
      if (minVal == null && maxVal == null) return null;
      const conditions: string[] = [
        `${c} IS NOT NULL`,
        `TRY_CAST(${c} AS DOUBLE) IS NOT NULL`,
      ];
      const rangeChecks: string[] = [];
      if (minVal != null) rangeChecks.push(`TRY_CAST(${c} AS DOUBLE) < ${Number(minVal)}`);
      if (maxVal != null) rangeChecks.push(`TRY_CAST(${c} AS DOUBLE) > ${Number(maxVal)}`);
      conditions.push(`(${rangeChecks.join(' OR ')})`);
      return `(${conditions.join(' AND ')})`;
    }

    case 'numeric':
      return `(${c} IS NOT NULL AND TRY_CAST(${c} AS DOUBLE) IS NULL)`;

    case 'date_format':
      return `(${c} IS NOT NULL AND TRY_CAST(${c} AS TIMESTAMP) IS NULL)`;

    case 'before_date': {
      const compareField = rule.field;
      if (!compareField) return null;
      const cf = q(compareField);
      return `(${c} IS NOT NULL AND ${cf} IS NOT NULL AND TRY_CAST(${c} AS TIMESTAMP) >= TRY_CAST(${cf} AS TIMESTAMP))`;
    }

    case 'after_date': {
      const compareField = rule.field;
      if (!compareField) return null;
      const cf = q(compareField);
      return `(${c} IS NOT NULL AND ${cf} IS NOT NULL AND TRY_CAST(${c} AS TIMESTAMP) <= TRY_CAST(${cf} AS TIMESTAMP))`;
    }

    default:
      return null;
  }
}

// ── Rule collection for single-scan validation ───────────────────────

interface ValidationRuleSpec {
  targetField: string;
  ruleType: string;
  severity: string;
  /** Rows matching this predicate are counted as violations. */
  violationPredicate: string;
}

function collectValidationRuleSpecs(
  viewName: string,
  columnRules: ColumnRules[],
): { specs: ValidationRuleSpec[]; pkCtes: string[] } {
  const v = q(viewName);
  const specs: ValidationRuleSpec[] = [];
  const pkCtes: string[] = [];
  let pkIndex = 0;

  for (const { columnName, rules, isNullable, isPrimaryKey } of columnRules) {
    // Structural: nullable check
    if (isNullable === false) {
      const violation = whereClause(columnName, 'not_empty', {});
      if (violation) {
        specs.push({
          targetField: columnName,
          ruleType: 'nullable',
          severity: 'error',
          violationPredicate: violation,
        });
      }
    }

    // Structural: primary key uniqueness (duplicate keys via CTE, counted in single agg scan)
    if (isPrimaryKey) {
      const c = q(columnName);
      const cteName = `__pk_dup_${pkIndex}`;
      pkIndex += 1;
      pkCtes.push(
        `${cteName} AS (SELECT ${c} AS pk_val FROM ${v} GROUP BY ${c} HAVING COUNT(*) > 1)`,
      );
      specs.push({
        targetField: columnName,
        ruleType: 'primary_key',
        severity: 'error',
        violationPredicate: `${c} IN (SELECT pk_val FROM ${cteName})`,
      });
    }

    // Explicit validation_rules
    for (const rule of rules) {
      const ruleType = canonicalRuleType(rule);
      if (!ruleType || ruleType === 'custom') continue;

      const violation = whereClause(columnName, ruleType, rule);
      if (!violation) continue;

      specs.push({
        targetField: columnName,
        ruleType,
        severity: defaultSeverity(ruleType, rule),
        violationPredicate: violation,
      });
    }
  }

  return { specs, pkCtes };
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Build a query that returns one summary row per rule.
 * Each row has: target_field, rule_type, severity, violation_count, total_rows.
 *
 * Uses one aggregate scan of the view (plus optional PK duplicate CTEs) instead of
 * per-rule full-table scans joined with UNION ALL.
 */
export function buildValidationSql(viewName: string, columnRules: ColumnRules[]): string {
  const v = q(viewName);
  const { specs, pkCtes } = collectValidationRuleSpecs(viewName, columnRules);

  if (specs.length === 0) {
    // Return an empty result set with the correct schema
    return `SELECT '' AS target_field, '' AS rule_type, 'info' AS severity, 0 AS violation_count, 0 AS total_rows WHERE FALSE`;
  }

  const violAliases = specs.map((_, index) => `__viol_${index}`);
  const aggSelects = [
    'COUNT(*) AS total_rows',
    ...specs.map(
      (spec, index) =>
        `COUNT(*) FILTER (WHERE ${spec.violationPredicate}) AS ${violAliases[index]}`,
    ),
  ];

  const cteParts = [
    ...pkCtes,
    `agg AS (\n  SELECT\n    ${aggSelects.join(',\n    ')}\n  FROM ${v}\n)`,
  ];

  const unions = specs.map(
    (spec, index) =>
      `SELECT ${esc(spec.targetField)} AS target_field, ${esc(spec.ruleType)} AS rule_type, ` +
      `${esc(spec.severity)} AS severity, ${violAliases[index]} AS violation_count, total_rows FROM agg`,
  );

  return `WITH ${cteParts.join(',\n')}\n${unions.join('\nUNION ALL\n')}`;
}

/**
 * Build a SELECT * query that returns the actual violating rows for a specific rule.
 * The SQL is set into the Monaco editor so users can inspect/modify it.
 */
export function buildViolationRowsSql(
  viewName: string,
  columnName: string,
  rule: SchemaRule,
  limit = 200,
): string {
  const v = q(viewName);
  const c = q(columnName);
  const ruleType = canonicalRuleType(rule);

  // Primary key needs a subquery — can't use whereClause which doesn't have the view name
  if (ruleType === 'primary_key') {
    return `SELECT * FROM ${v} WHERE ${c} IN (SELECT ${c} FROM ${v} GROUP BY ${c} HAVING COUNT(*) > 1) ORDER BY ${c} LIMIT ${limit}`;
  }

  // Nullable structural check
  if (ruleType === 'nullable') {
    return `SELECT * FROM ${v} WHERE ${c} IS NULL OR TRIM(CAST(${c} AS VARCHAR)) = '' LIMIT ${limit}`;
  }

  const where = whereClause(columnName, ruleType, rule);

  if (!where) {
    return `SELECT * FROM ${v} LIMIT ${limit}`;
  }

  return `SELECT * FROM ${v} WHERE ${where} LIMIT ${limit}`;
}

/**
 * Extract column rules from a target_schema_snapshot for a given target table.
 * Handles the reverse-mapping from view name (__mapped_X__) → table name (X).
 */
export function extractColumnRules(
  targetSchemaSnapshot: Record<string, any>,
  viewName: string,
): ColumnRules[] {
  const tables = targetSchemaSnapshot?.tables;
  if (!tables || typeof tables !== 'object') return [];

  // Reverse-map: __mapped_adverse_events__ → adverse_events
  const stripped = viewName
    .replace(/^__mapped_/, '')
    .replace(/__$/, '')
    .toLowerCase();

  // Try exact match first, then normalized match
  let tableDef: any = null;
  let matchedTableName: string | null = null;

  for (const [name, def] of Object.entries(tables)) {
    if (name === stripped || name.toLowerCase() === stripped) {
      tableDef = def;
      matchedTableName = name;
      break;
    }
    // Also try with underscore normalization
    const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (normalized === stripped) {
      tableDef = def;
      matchedTableName = name;
      break;
    }
  }

  if (!tableDef || !matchedTableName) return [];

  const columns = tableDef.columns;
  if (!columns) return [];

  let columnsArray: any[] = [];

  if (Array.isArray(columns)) {
    columnsArray = columns;
  } else if (columns && typeof columns === 'object') {
    columnsArray = Object.entries(columns).map(([key, value]) => {
        if (value && typeof value === 'object') {
          const valueObj = value as any;
          if (!valueObj.name) {
            return { ...value, name: key };
          }
          return value;
        }
        return { name: key, type: value, validation_rules: [] };
      });
  }

    if (columnsArray.length === 0) return [];
  return columnsArray
    .filter((col: any) => col && typeof col === 'object' && col.name)
    .map((col: any) => ({
      columnName: col.name,
      rules: Array.isArray(col.validation_rules) ? col.validation_rules : [],
      isNullable: col.is_nullable ?? col.nullable ?? true,
      isPrimaryKey: col.is_primary_key ?? col.primary_key ?? false,
    }));
}

/** Keep only rules for columns present in the materialized mapped view. */
export function filterColumnRulesToView(
  columnRules: ColumnRules[],
  viewColumnNames: readonly string[],
): ColumnRules[] {
  if (!viewColumnNames.length) return [];
  const present = new Set(viewColumnNames);
  return columnRules.filter((cr) => present.has(cr.columnName));
}
